import { css, html, LitElement } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api } from '../api.js';
import { EventTask } from '../contexts.js';
import { CHECK } from '@flakiness/shared/common/utils.js';

export type JsonRepoUpload = {
  jsonRepoPath: string,
}

@customElement('pick-json-repo')
class PickJsonRepo extends LitElement {
  @state() private _fileContent?: string;
  @state() private _fileName?: string;
  @state() private _uploading = false;
  @state() private _uploadError?: string;

  private _uploadTask = new EventTask(this, 'json-repo', {
    args: () => [this._fileContent] as const,
    task: async ([content]) => {
      CHECK(content);
      this._uploading = true;
      this._uploadError = undefined;
      try {
        const result = await api.administration.uploadJsonRepo.POST({ content });
        const jsonRepoUpload: JsonRepoUpload = { jsonRepoPath: result.filePath };
        return jsonRepoUpload;
      } catch (e) {
        this._uploadError = e instanceof Error ? e.message : 'Upload failed';
        throw e;
      } finally {
        this._uploading = false;
      }
    },
  });

  private _onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file)
      return;
    this._fileName = file.name;
    const reader = new FileReader();
    reader.onload = () => {
      this._fileContent = reader.result as string;
    };
    reader.readAsText(file);
  }

  override render() {
    return html`
      <v-box style="align-items: start; gap: var(--sl-spacing-small);">
        <p>Select a <code>Git.JSONRepo</code> file to upload. The file will be saved on the server and used as the git data source.</p>
        <h-box style="align-items: center; gap: var(--sl-spacing-small);">
          <sl-button variant="neutral" @click=${() => this.renderRoot.querySelector('input')?.click()}>
            <sl-icon slot="prefix" name="file-earmark-arrow-up"></sl-icon>
            Choose file
          </sl-button>
          <input type="file" accept=".json" @change=${this._onFileSelected} hidden>
          ${this._fileName ? html`<span>${this._fileName}</span>` : html`<span style="color: var(--sl-color-neutral-400)">No file selected</span>`}
        </h-box>
        ${this._uploading ? html`<sl-spinner></sl-spinner>` : ''}
        ${this._uploadError ? html`<sl-alert variant="danger" open>${this._uploadError}</sl-alert>` : ''}
        ${this._uploadTask.value ? html`<sl-alert variant="success" open>Uploaded to <code>${this._uploadTask.value.jsonRepoPath}</code></sl-alert>` : ''}
      </v-box>
    `;
  }

  static styles = [css`
    code {
      font-family: var(--sl-font-mono);
      font-size: var(--sl-font-size-small);
    }
  `];
}
